"use client";

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PlatformError } from "@/lib/api/client";
import { platformFetch } from "@/lib/api/browser";
import { relativeTime } from "@/lib/api/mappers";
import { messageFor } from "@/lib/api/messages";
import { PAGE_LIMIT_MAX_100 } from "@/lib/api/paginate";
import { rewardLabels, toRewardHistory, type ApiRewardTransaction } from "@/lib/api/reward-history";
import { useIdentity } from "@/lib/api/session";
import { Panel, PanelHeader } from "@/components/ui/primitives";
import { SignedOut } from "@/components/ui/SignedOut";
import { SectionEmpty, SectionError } from "@/components/ui/states";
import { Pills, TABLE_HEAD, TABLE_ROW, VerifyLink } from "./parts";

/* The player's reward history, as the kit's account rewards frame draws it and
   as the whitelabel reads it: the Live Rewards feed filtered to one account.

   It used to be built from `/rewards/history`, a claim ledger that knows two
   types, so every cashback, bounty and free-spin payout — which the platform
   files under one "incentive-claim" kind — printed that raw string for a type.
   The feed carries the reward's type in `name`; src/lib/api/reward-history.ts
   is where that is read, and where custom rewards are left out.

   The kit's NAME column is not drawn. The feed has the reward's type and not
   the bonus's own name, and a column that repeated the type would say nothing. */

const ALL = "All Rewards";

export function RewardsHistory() {
  const { identity, isSignedIn, isPending } = useIdentity();
  const [filter, setFilter] = useState(ALL);

  const history = useQuery({
    queryKey: ["rewards", "transactions", "account", identity?.id],
    enabled: isSignedIn && Boolean(identity?.id),
    staleTime: 30_000,
    queryFn: async () =>
      (
        await platformFetch<{ data: ApiRewardTransaction[] }>("rewards/transactions", {
          accountId: identity?.id,
          limit: PAGE_LIMIT_MAX_100,
        })
      ).data,
  });

  const rows = toRewardHistory(history.data ?? []);
  const labels = rewardLabels(rows);
  /* A filter whose type has left the list — a refetch, another account —
     falls back to everything rather than to an empty table. */
  const active = labels.includes(filter) ? filter : ALL;
  const shown = active === ALL ? rows : rows.filter((row) => row.label === active);
  const now = Date.now();

  return (
    <Panel>
      <PanelHeader icon="gift" title="Reward History" />

      {isPending && <div className="m-[16px] h-24 animate-pulse rounded-lg bg-elevation-2" />}

      {!isPending && !isSignedIn && (
        <SignedOut
          title="Sign in to see your reward history"
          body="Every reward paid to your account: rakeback, cashback, bounties, free spins and free money."
        />
      )}

      {isSignedIn && history.isPending && <div className="m-[16px] h-24 animate-pulse rounded-lg bg-elevation-2" />}

      {isSignedIn && history.isError && (
        <SectionError
          title="Reward history didn’t load"
          body={messageFor(
            history.error instanceof PlatformError ? history.error : new PlatformError(0, "network", ""),
            "rewards",
          )}
        />
      )}

      {isSignedIn && history.isSuccess && rows.length === 0 && (
        <SectionEmpty icon="gift" title="No rewards yet" body="Rewards appear here once they are paid to your account." />
      )}

      {isSignedIn && rows.length > 0 && (
        <>
          {labels.length > 1 && <Pills options={[ALL, ...labels]} value={active} onChange={setFilter} />}

          <div
            role="region"
            aria-label="Reward history"
            tabIndex={0}
            className={`scrollbar-none overflow-x-auto px-[16px] pb-[16px] ${labels.length > 1 ? "" : "pt-[16px]"}`}
          >
            <table className="w-full min-w-[520px] border-collapse overflow-hidden rounded-xl">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="pl-[14px] text-left">TYPE</th>
                  <th className="text-left">TIME</th>
                  <th className="text-right">AMOUNT</th>
                  <th className="pr-[14px] text-right">VERIFY</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id} className={TABLE_ROW}>
                    <td className="pl-[14px]">{row.label}</td>
                    <td className="whitespace-nowrap">{relativeTime(row.createdAt, now)}</td>
                    <td className="whitespace-nowrap text-right tabular-nums text-highlight">{row.amount}</td>
                    <td className="pr-[14px] text-right">
                      <VerifyLink hash={row.hash} fullHash={row.fullHash} chainId={row.chainId} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
